import {
    CSSProperties,
    FC,
    Fragment,
    PropsWithChildren,
    ReactNode,
    useContext,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import classnames from 'classnames';

import { throttle } from '@hh.ru/magritte-common-func-utils';
import { CustomScrollContext, CustomScrollEvent } from '@hh.ru/magritte-internal-custom-scroll';
import { InternalLayerName } from '@hh.ru/magritte-internal-layer-name';
import { Divider } from '@hh.ru/magritte-ui-divider';
import { Layer } from '@hh.ru/magritte-ui-layer';
import { NavigationBarItem } from '@hh.ru/magritte-ui-navigation-bar/NavigationBarItem';
import { NavigationBarItems } from '@hh.ru/magritte-ui-navigation-bar/NavigationBarItems';
import {
    NavigationBarProps,
    NavigationBarComponentProps,
    NavigationBarSize,
} from '@hh.ru/magritte-ui-navigation-bar/types';
import { ProgressBar } from '@hh.ru/magritte-ui-progress-bar';

import styles from './navigation-bar.less';

// ключи нужны для того чтобы не было ремаунта options при изменении верстки с large на standard
const ITEMS_CONTAINER_KEY = 'items-container';
const TITLE_CONTAINER_KEY = 'title-container';
const OPTIONS_CONTAINER_KEY = 'options-container';
const OPTIONS_KEY = 'options';
const DUMMY_KEY = 'dummy';
const SCROLL_THROTTLE_MS = 50;

const calcTitleOpacity = (opacity: number) => `${Math.pow(opacity, 2)}`;

const calcDummyOpacity = (opacity: number) => `${1 - 2 * opacity}`;

const findScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
    if (!element) {
        return null;
    }

    let parentElement = element.parentElement;
    while (parentElement !== null) {
        const { overflowY } = window.getComputedStyle(parentElement);
        if (overflowY.includes('auto') || overflowY.includes('scroll')) {
            return parentElement;
        }
        parentElement = parentElement.parentElement;
    }

    return null;
};

const renderStringNode = (
    node: ReactNode | ((actualSize: NavigationBarSize) => ReactNode),
    actualSize: NavigationBarSize
) => {
    return typeof node === 'function' ? node(actualSize) : node;
};

const StandardTitle: FC<NavigationBarProps & { 'data-qa'?: string }> = ({
    title,
    subtitle,
    left,
    right,
    ellipsisRenderFunc,
    'data-qa': dataQa,
}) => {
    return (
        <div className={styles.spread} data-qa={dataQa} role="menu">
            <div className={styles.left}>{left && <NavigationBarItem role="menuitem" item={left} />}</div>
            <div className={styles.right}>
                <div className={styles.halfTitleWrapper}>
                    <div className={styles.titleWrapper}>
                        {title && (
                            <Fragment>
                                <div
                                    className={classnames(styles.title, {
                                        [styles.titleWithoutSubtitle]: !subtitle,
                                    })}
                                >
                                    {renderStringNode(title, 'standard')}
                                </div>
                                {subtitle && (
                                    <div className={styles.subtitle}>{renderStringNode(subtitle, 'standard')}</div>
                                )}
                            </Fragment>
                        )}
                    </div>
                </div>
                <div className={styles.rightDummy}>
                    <NavigationBarItems items={right} size="standard" renderFunc={ellipsisRenderFunc} />
                </div>
            </div>
        </div>
    );
};

const NavigationBarLayer: FC<PropsWithChildren> = ({ children }) => {
    return <Layer layer={InternalLayerName.NavigationBar}>{children}</Layer>;
};

type State = {
    actualSize: NavigationBarSize;
    // marginTop, который добавляется при изменении размера с large на standard чтобы не менялась высота родителя
    heightCompensation: number;
    heightObservers: Array<VoidFunction>;
    heightWithLargeSize: number | null;
    heightWithStandardSize: number;
    // используются для кастомного скролла
    titleTranslateY: number;
    optionsTranslateY: number;
    // прогресс перехода между large и standard размерами (0..1), используется для изменения прозрачности
    transitionProgress: number;
    skipFirstResizeCallback: boolean;
};

export const NavigationBarComponent: FC<NavigationBarComponentProps> = ({
    size = 'standard',
    title,
    subtitle,
    left,
    progress,
    right,
    options,
    allowOptionsScroll,
    showDivider: showDividerMode,
    ellipsisRenderFunc,
    inLayer,
    forwardedRef,
}) => {
    const scrollContext = useContext(CustomScrollContext);

    const [actualSize, setActualSize] = useState(size);
    const [showDivider, setShowDivider] = useState(showDividerMode === 'always');
    const stateRef = useRef<State>({
        actualSize: size,
        heightCompensation: 0,
        heightObservers: [],
        heightWithLargeSize: null,
        heightWithStandardSize: 0,
        titleTranslateY: 0,
        optionsTranslateY: 0,
        transitionProgress: 1,
        skipFirstResizeCallback: true,
    });

    const itemsContainerRef = useRef<HTMLDivElement>(null);
    const titleContainerRef = useRef<HTMLDivElement>(null);
    const optionsContainerRef = useRef<HTMLDivElement>(null);
    const largeTitleRef = useRef<HTMLDivElement>(null);
    const dividerRef = useRef<HTMLDivElement>(null);
    const dummyRef = useRef<HTMLDivElement>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const scrollContainerRef = useRef<HTMLElement | null | undefined>();

    useEffect(() => {
        if (!resizeObserverRef.current) {
            resizeObserverRef.current = new ResizeObserver(() => {
                if (stateRef.current.skipFirstResizeCallback) {
                    stateRef.current.skipFirstResizeCallback = false;
                    return;
                }
                stateRef.current.heightObservers.forEach((observer) => observer());
            });
        }

        const resizeObserver = resizeObserverRef.current;
        const titleContainer = titleContainerRef.current;
        const optionsContainer = optionsContainerRef.current;

        titleContainer !== null && resizeObserver.observe(titleContainer);
        optionsContainer !== null && resizeObserver.observe(optionsContainer);

        return () => {
            titleContainer !== null && resizeObserver.unobserve(titleContainer);
            optionsContainer !== null && resizeObserver.unobserve(optionsContainer);
        };
    }, [actualSize]);

    useEffect(() => {
        setShowDivider(showDividerMode === 'always');
    }, [showDividerMode]);

    useEffect(() => {
        const itemsContainer = itemsContainerRef.current;
        const titleContainer = titleContainerRef.current;
        const titleContainerHeight = titleContainer?.clientHeight ?? 0;
        const optionsContainer = optionsContainerRef.current;
        const optionsContainerHeight = optionsContainer?.clientHeight ?? 0;

        if (actualSize === 'large') {
            const itemsContainerHeight = itemsContainer?.clientHeight ?? 0;
            const dummyHeight = dummyRef.current?.clientHeight ?? 0;
            stateRef.current.heightWithLargeSize = itemsContainerHeight + titleContainerHeight + optionsContainerHeight;
            stateRef.current.heightWithStandardSize = dummyHeight + optionsContainerHeight;
        }

        if (actualSize === 'standard') {
            stateRef.current.heightWithStandardSize = titleContainerHeight + optionsContainerHeight;
        }

        const handleScroll = (scrollTop: number, maxScrollTop: number) => {
            if (
                size === 'large' &&
                stateRef.current.heightWithLargeSize !== null &&
                stateRef.current.heightWithLargeSize - stateRef.current.heightWithStandardSize <= maxScrollTop
            ) {
                const visibleHeight = stateRef.current.heightWithLargeSize - scrollTop;

                const _transitionProgress =
                    (visibleHeight - stateRef.current.heightWithStandardSize) /
                    (stateRef.current.heightWithLargeSize - stateRef.current.heightWithStandardSize);
                const transitionProgress = Math.max(Math.min(_transitionProgress, 1), 0);

                if (Math.abs(stateRef.current.transitionProgress - transitionProgress) > Number.EPSILON) {
                    stateRef.current.transitionProgress = Math.max(Math.min(transitionProgress, 1), 0);
                    if (itemsContainer !== null) {
                        itemsContainer.style.setProperty(
                            '--navigation-bar-transition-opacity',
                            calcTitleOpacity(transitionProgress)
                        );
                    }
                    if (largeTitleRef.current !== null) {
                        largeTitleRef.current.style.opacity = calcTitleOpacity(transitionProgress);
                    }
                    if (dividerRef.current !== null) {
                        dividerRef.current.style.opacity = calcDummyOpacity(transitionProgress);
                    }
                    if (dummyRef.current !== null) {
                        dummyRef.current.style.opacity = calcDummyOpacity(transitionProgress);
                    }
                }

                if (actualSize === 'large' && visibleHeight < stateRef.current.heightWithStandardSize) {
                    stateRef.current.actualSize = 'standard';
                    stateRef.current.heightCompensation =
                        stateRef.current.heightWithLargeSize - stateRef.current.heightWithStandardSize;
                    setActualSize('standard');
                }

                if (actualSize === 'standard' && visibleHeight > stateRef.current.heightWithStandardSize) {
                    stateRef.current.actualSize = 'large';
                    stateRef.current.heightCompensation = 0;
                    setActualSize('large');
                }
            }

            if (
                (size === 'standard' || stateRef.current.actualSize === 'standard') &&
                showDividerMode === 'with-scroll'
            ) {
                const visibleHeight = stateRef.current.heightWithStandardSize - scrollTop;
                setShowDivider(Math.abs(stateRef.current.heightWithStandardSize - visibleHeight) > Number.EPSILON);
            }
        };

        if (scrollContext !== null) {
            const handleCustomScroll = (event: CustomScrollEvent) => {
                handleScroll(event.scrollTop, event.maxScrollTop);

                const applyTransform = stateRef.current.actualSize === actualSize;

                if (titleContainer !== null) {
                    const titleTranslateY =
                        stateRef.current.actualSize === 'large'
                            ? -event.scrollTop
                            : -stateRef.current.heightCompensation;
                    if (Math.abs(stateRef.current.titleTranslateY - titleTranslateY) > Number.EPSILON) {
                        stateRef.current.titleTranslateY = titleTranslateY;
                        if (applyTransform) {
                            titleContainer.style.transform = `translate3d(0, ${titleTranslateY}px, 0)`;
                        }
                    }
                }

                if (optionsContainerRef.current !== null) {
                    const maxOptionsTranslateY =
                        stateRef.current.heightCompensation + optionsContainerHeight - (showDivider ? 1 : 0);
                    const optionsTranslateY =
                        stateRef.current.actualSize === 'large'
                            ? -event.scrollTop
                            : -(allowOptionsScroll
                                  ? Math.min(event.scrollTop, maxOptionsTranslateY)
                                  : stateRef.current.heightCompensation);
                    if (Math.abs(stateRef.current.optionsTranslateY - optionsTranslateY) > Number.EPSILON) {
                        stateRef.current.optionsTranslateY = optionsTranslateY;
                        if (applyTransform) {
                            optionsContainerRef.current.style.transform = `translate3d(0, ${optionsTranslateY}px, 0)`;
                        }
                    }
                }
            };
            scrollContext.subscribe(handleCustomScroll);
            return () => {
                scrollContext.unsubscribe(handleCustomScroll);
            };
        }

        if (scrollContainerRef.current === undefined) {
            scrollContainerRef.current = findScrollContainer(titleContainer);
        }

        if (scrollContainerRef.current !== null) {
            const scrollContainer = scrollContainerRef.current;
            const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.offsetHeight;
            const handleNativeScroll = throttle(
                () => handleScroll(scrollContainer.scrollTop, maxScrollTop),
                SCROLL_THROTTLE_MS
            );
            scrollContainer.addEventListener('scroll', handleNativeScroll);
            return () => {
                handleNativeScroll.cancel();
                scrollContainer.removeEventListener('scroll', handleNativeScroll);
            };
        }

        const maxScrollTop = document.documentElement.scrollHeight - (visualViewport as VisualViewport).height;
        const handleNativeScroll = throttle(
            () => handleScroll((visualViewport as VisualViewport).pageTop, maxScrollTop),
            SCROLL_THROTTLE_MS
        );
        window.addEventListener('scroll', handleNativeScroll);
        return () => {
            handleNativeScroll.cancel();
            window.removeEventListener('scroll', handleNativeScroll);
        };
    }, [size, actualSize, setActualSize, allowOptionsScroll, showDivider, showDividerMode, scrollContext]);

    useImperativeHandle(forwardedRef, () => {
        return {
            /**
             * Фактическая высота, которую компонент занимает в родительском контейнере
             * Может отличаться от видимой высоты за счет marginTop (stateRef.current.heightCompensation)
             */
            get layoutHeight() {
                return stateRef.current.heightWithLargeSize ?? stateRef.current.heightWithStandardSize;
            },
            /**
             * Высота видимой части компонента
             * Может отличаться от фактической высоты
             */
            get visibleHeight() {
                return (
                    (stateRef.current.actualSize === 'large' ? stateRef.current.heightWithLargeSize : null) ??
                    stateRef.current.heightWithStandardSize
                );
            },
            addHeightObserver: (observer) => {
                stateRef.current.heightObservers.push(observer);
            },
            removeHeightObserver: (observer) => {
                stateRef.current.heightObservers = stateRef.current.heightObservers.filter(
                    (_observer) => _observer !== observer
                );
            },
        };
    }, []);

    const Wrapper = inLayer ? NavigationBarLayer : Fragment;
    const titleProps = { title, subtitle, left, progress, right, ellipsisRenderFunc };
    const translateY = (value: number) => (scrollContext !== null ? `translate3d(0, ${value}px, 0)` : undefined);

    if (actualSize === 'large') {
        return (
            <Wrapper>
                <div
                    key={ITEMS_CONTAINER_KEY}
                    className={classnames(styles.fixedItemsContainer, styles.backgroundContainer)}
                    ref={itemsContainerRef}
                    style={
                        {
                            '--navigation-bar-transition-opacity': calcTitleOpacity(
                                stateRef.current.transitionProgress
                            ),
                        } as CSSProperties
                    }
                >
                    <div className={styles.topPaddingOverride}>
                        <div className={styles.spread} data-qa="navigation-bar-items" role="menu">
                            <div className={styles.left}>
                                {left && <NavigationBarItem role="menuitem" item={left} />}
                            </div>
                            <div className={styles.right}>
                                {progress && (
                                    <div className={classnames(styles.progress, styles.transitionWithOpacity)}>
                                        <div className={styles.availableWidthWrapper}>
                                            <ProgressBar {...progress} />
                                        </div>
                                    </div>
                                )}
                                <NavigationBarItems items={right} size="large" renderFunc={ellipsisRenderFunc} />
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    key={TITLE_CONTAINER_KEY}
                    className={classnames(styles.scrollableTitleContainer)}
                    data-qa="navigation-bar-container navigation-bar-container_large"
                    ref={titleContainerRef}
                    style={{ transform: translateY(stateRef.current.titleTranslateY) }}
                >
                    <div
                        className={classnames(styles.largeTitleContainer)}
                        ref={largeTitleRef}
                        style={{ opacity: calcTitleOpacity(stateRef.current.transitionProgress) }}
                    >
                        {title && (
                            <Fragment>
                                <div className={styles.title}>{renderStringNode(title, actualSize)}</div>
                                {subtitle && (
                                    <div className={styles.subtitle}>{renderStringNode(subtitle, actualSize)}</div>
                                )}
                            </Fragment>
                        )}
                    </div>
                </div>

                {options && (
                    <div
                        key={OPTIONS_CONTAINER_KEY}
                        className={classnames(styles.scrollableOptionsContainer, styles.backgroundContainer)}
                        data-qa="navigation-bar-options"
                        ref={optionsContainerRef}
                        style={{ transform: translateY(stateRef.current.optionsTranslateY) }}
                    >
                        <div key={OPTIONS_KEY} className={styles.options}>
                            {options}
                        </div>
                        {showDividerMode && (
                            <div
                                ref={dividerRef}
                                style={{ opacity: calcDummyOpacity(stateRef.current.transitionProgress) }}
                            >
                                <Divider />
                            </div>
                        )}
                    </div>
                )}

                <div
                    key={DUMMY_KEY}
                    className={styles.dummy}
                    ref={dummyRef}
                    style={{
                        opacity: calcDummyOpacity(stateRef.current.transitionProgress),
                        position: scrollContext !== null ? 'absolute' : 'fixed',
                    }}
                >
                    <div className={styles.topPaddingOverride}>
                        <div className={classnames(styles.standardTitleContainer)}>
                            <StandardTitle {...titleProps} />
                        </div>
                    </div>
                </div>
            </Wrapper>
        );
    }

    if (actualSize === 'standard') {
        return (
            <Wrapper>
                <div key={ITEMS_CONTAINER_KEY} />

                <div
                    key={TITLE_CONTAINER_KEY}
                    className={classnames(styles.fixedTitleContainer, styles.backgroundContainer)}
                    data-qa="navigation-bar-container navigation-bar-container_standard"
                    ref={titleContainerRef}
                    style={{
                        marginTop: `${stateRef.current.heightCompensation}px`,
                        transform: translateY(stateRef.current.titleTranslateY),
                    }}
                >
                    <div className={styles.topPaddingOverride}>
                        <div className={classnames(styles.standardTitleContainer)}>
                            <StandardTitle {...titleProps} data-qa="navigation-bar-items" />
                        </div>
                    </div>
                </div>

                {allowOptionsScroll && (
                    <div
                        key={OPTIONS_CONTAINER_KEY}
                        className={classnames(styles.scrollableOptionsContainer, styles.backgroundContainer)}
                        data-qa="navigation-bar-options"
                        ref={optionsContainerRef}
                        style={{ transform: translateY(stateRef.current.optionsTranslateY) }}
                    >
                        {options && (
                            <div key={OPTIONS_KEY} className={styles.options}>
                                {options}
                            </div>
                        )}
                        {showDivider && <Divider />}
                    </div>
                )}

                {!allowOptionsScroll && (
                    <div
                        key={OPTIONS_CONTAINER_KEY}
                        className={classnames(styles.fixedOptionsContainer, styles.backgroundContainer)}
                        data-qa="navigation-bar-options"
                        ref={optionsContainerRef}
                        style={{ transform: translateY(stateRef.current.optionsTranslateY) }}
                    >
                        {options && (
                            <div key={OPTIONS_KEY} className={styles.options}>
                                {options}
                            </div>
                        )}
                        {showDivider && <Divider />}
                    </div>
                )}
            </Wrapper>
        );
    }

    return null;
};
